import { useCallback, useEffect, useState } from "react";
import { MDBBtn, MDBSpinner } from "mdb-react-ui-kit";
import "./ExhibitionWorkspace.css";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import ExhibicionCreateModal from "Projects/EXHI_exhibiciones_project/Components/ExhibicionCreateModal/ExhibicionCreateModal";
import ExhibicionShareModal from "Projects/EXHI_exhibiciones_project/Components/ExhibicionShareModal/ExhibicionShareModal";
import ExhibitionDetail from "Projects/EXHI_exhibiciones_project/Components/ExhibitionDetail/ExhibitionDetail";

/**
 * ExhibitionWorkspace — Componente embebible que muestra las exhibiciones de una app.
 *
 * Props:
 *   hostAppCode   {string}   — Filtrar por appCode de contexto (opcional)
 *   canCreate     {boolean}  — Mostrar botón "Nueva exhibición"
 *   canManage     {boolean}  — Puede editar/eliminar cualquier exhibición
 *   currentUserId {number}   — ID del usuario actual (para determinar propiedad)
 *   registeredApps {string[]} — Apps donde el usuario está registrado (para crear modal)
 *   onLoading     {Function} — Callback cuando cambia el estado de carga
 */
export default function ExhibitionWorkspace({
  hostAppCode = null,
  canCreate = false,
  canManage = false,
  currentUserId = 0,
  registeredApps = [],
  onLoading,
}) {
  const [exhibiciones, setExhibiciones] = useState([]);
  const [loading, setLoading] = useState(false);
  // selected: { idExhibicion, canManageThis } | null
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // { idExhibicion, appCodes }

  const load = useCallback(async () => {
    setLoading(true);
    onLoading && onLoading(true);
    try {
      const query = hostAppCode ? `?appCode=${encodeURIComponent(hostAppCode)}` : "";
      const res = await doFetchJson("EXHI", `exhibiciones${query}`, { method: "GET" });
      if (res?.STATE === "SUCCESS") {
        setExhibiciones(Array.isArray(res.DATA) ? res.DATA : []);
      } else {
        toast.show({ message: res?.MESSAGE || "Error al cargar exhibiciones", type: "danger" });
      }
    } catch {
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setLoading(false);
      onLoading && onLoading(false);
    }
  }, [hostAppCode]);

  const handleCreateClose = useCallback(() => setCreateOpen(false), []);
  const handleShareClose = useCallback(() => setShareTarget(null), []);
  const handleCreated = useCallback((id) => {
    setCreateOpen(false);
    load();
    // Si acabamos de crearlo, somos el propietario → podemos gestionarlo
    if (id) setSelected({ idExhibicion: id, canManageThis: true });
  }, [load]);

  useEffect(() => { load(); }, [load]);

  function handleShareClick(e, ex) {
    e.stopPropagation();
    setShareTarget({ idExhibicion: ex.idExhibicion, appCodes: ex.appCodes || [] });
  }

  if (selected) {
    return (
      <ExhibitionDetail
        idExhibicion={selected.idExhibicion}
        canManage={selected.canManageThis}
        onBack={() => { setSelected(null); load(); }}
      />
    );
  }

  return (
    <div className="exhi-workspace-list">
      {canCreate && (
        <div className="exhi-workspace-header">
          <span />
          <MDBBtn size="sm" onClick={() => setCreateOpen(true)}>
            + Nueva exhibición
          </MDBBtn>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <MDBSpinner />
        </div>
      ) : exhibiciones.length === 0 ? (
        <div className="exhi-workspace-empty">No hay exhibiciones todavía.</div>
      ) : (
        <div className="exhi-workspace-grid">
          {exhibiciones.map((ex) => {
            const isOwner = currentUserId > 0 && ex.idCreador === currentUserId;
            // canManage global (admin/manager) o el usuario es propietario/receptor del share
            const canManageThis = canManage || isOwner || !!ex.isSharedWithMe;
            return (
              <div
                key={ex.idExhibicion}
                className="exhi-workspace-card"
                onClick={() => setSelected({ idExhibicion: ex.idExhibicion, canManageThis })}
              >
                <div className="exhi-workspace-card-title">{ex.titulo}</div>
                <div className="exhi-workspace-card-meta">
                  <span className={`exhi-workspace-badge ${ex.estado}`}>{ex.estado}</span>
                  {ex.isSharedWithMe && (
                    <span className="exhi-workspace-badge-shared">Compartida</span>
                  )}
                  <span>{ex.numSecciones ?? 0} secciones</span>
                  {ex.duracionTotal != null && (
                    <span>
                      {Math.floor(ex.duracionTotal / 60)}:{String(Math.floor(ex.duracionTotal % 60)).padStart(2, "0")} min
                    </span>
                  )}
                  {ex.creadoPor && <span style={{ opacity: 0.7 }}>@{ex.creadoPor}</span>}
                </div>
                {isOwner && (
                  <div className="exhi-workspace-card-actions" onClick={(e) => e.stopPropagation()}>
                    <MDBBtn
                      size="sm"
                      color="link"
                      className="exhi-share-btn"
                      onClick={(e) => handleShareClick(e, ex)}
                    >
                      Compartir
                    </MDBBtn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ExhibicionCreateModal
        show={createOpen}
        setShow={handleCreateClose}
        hostAppCode={hostAppCode}
        registeredApps={registeredApps}
        onCreated={handleCreated}
      />

      {shareTarget && (
        <ExhibicionShareModal
          show={!!shareTarget}
          setShow={handleShareClose}
          idExhibicion={shareTarget.idExhibicion}
          appCodes={shareTarget.appCodes}
          onChanged={load}
        />
      )}
    </div>
  );
}
